import moment from 'moment';
import ApiService from '@/services/apiService';
import NiotaClassicApiService from '@/services/niotaClassicApiService';
import { mapGetters, mapState } from 'vuex';
import customColorMixin from '@/mixins/customColor';
import iconsMixin from '../dtwin/state/bridge/iconsMixin';
import packetChartMixin from './packetChartMixin';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
export default {
  name: 'CoreVirtualDeviceTab',
  mixins: [iconsMixin, customColorMixin, packetChartMixin],
  props: {
    dtwin: {
      type: Object,
      required: true
    },
    deviceTemplate: {
      type: Object,
      required: false,
      default: null
    },
    twinDataStates: {
      type: Array,
      required: false,
      default: () => []
    },
    bridgeReadMethod: {
      type: String,
      required: false,
      default: null
    },
    bridgeUpdateMethod: {
      type: String,
      required: false,
      default: null
    },
    bridgeCreateFields: {
      type: Array,
      required: true
    },
    bridgeCreateParameters: {
      type: Object,
      required: false,
      default: () => ({})
    },
    connectedBridge: {
      type: Object,
      required: false,
      default: () => ({})
    },
    bridgeManifest: {
      type: Object,
      required: false,
      default: () => ({})
    },
    hasTwinManagePermission: {
      type: Boolean,
      required: false,
      default: false
    },
    hasVirtualDeviceKeyReaderPermission: {
      type: Boolean,
      required: false,
      default: false
    },
    // do discourage the user from changing the device-type after it has been set
    // so compare dtwin.data.virtualDevice.deviceTypeId with this prop
    savedDeviceTypeId: {
      type: Number,
      required: false,
      default: null
    },
    // do discourage the user from changing the device-driver after it has been set
    // so compare dtwin.data.virtualDevice.deviceDriverId with this prop
    savedDeviceDriverId: {
      type: Number,
      required: false,
      default: null
    }
  },
  data: function () {
    return {
      timeRange: {
        // from: 7 days before
        from: new Date(new Date() - 86400000 * 7) / 1000,
        to: new Date() / 1000,
        // does the time-range range to "NOW", i.e. should the packet-history be a live-viewer?
        timeRangeToNow: true,
        // distinguish whether USER picked a time-range or this is an auto-refresh
        isAutoRefresh: true
      },
      totalUplinkPacketCount: null,
      currentPacket: null,
      currentParsedPacket: null,
      panelsStartExpanded: false,
      deviceGroups: [],
      newDeviceGroup: '',
      isLoadingTotalCount: false,
      // in case the bridge has no update-method but a read-method, read the fields into here
      // otherwise "just print out the existing fields"
      deviceDataFromBridge: null,
      selectedBridge: null,
      bridgeTypes: [],
      deviceTypes: [],
      deviceDriver: null,
      selectedDeviceType: {},
      deviceTypeChangedPrompt: false,
      deviceTypeSyncPrompt: false,
      deviceTemplateUnlinkPrompt: false,
      isUnlocked: false,
      downlinkFormValid: false,
      downlinkParameters: {},
      deviceDriversSearchResult: [],
      isSearchingDeviceDrivers: false,
      deviceDriversSearchInput: '',
      downlinksRenderCacheBuster: 0
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'language']),
    ...mapGetters({
      isFeatureFlagEnabled: 'app/isFeatureFlagEnabled'
    }),
    isDeviceDriverActive() {
      return !this.isFeatureFlagEnabled('env_dispatcher_devicetype_fallback');
    },
    isCreatedByTemplate() {
      return !!this.deviceTemplate;
    },
    templateGroups() {
      return this.deviceTemplate ? this.deviceTemplate.deviceMeta.groups : [];
    },
    predefinedDownlinks() {
      if (this.bridgeManifest.displayName !== 'Firefly' && this.bridgeManifest.displayName !== 'Loriot') {
        // no matter what the manifest says, predefined downlink selection currently only works for firefly and Loriot
        return [];
      }
      const showPredefinedDownlinks = get(this.bridgeManifest, 'showPredefinedDownlinks');
      if (!showPredefinedDownlinks) {
        return [];
      }
      let driverVersions = get(this.deviceDriver, 'versions', []);
      if (!Array.isArray(driverVersions)) {
        driverVersions = [];
      }
      const activeDriverVersion = driverVersions.find(v => v.version === 'default');
      const enumValues = get(this.downlinkMethodParameters, 'encoding.values', []);
      const deviceTypeDownlinks = get(this.selectedDeviceType, 'properties.downlinks');
      const deviceDriverDownlinks = get(activeDriverVersion, 'downlinks');
      let rawDownlinks = [];
      if (this.isDeviceDriverActive) {
        rawDownlinks = deviceDriverDownlinks || [];
      } else {
        rawDownlinks = deviceTypeDownlinks || [];
      }
      const downlinks = rawDownlinks.map(downlink => {
        if (!enumValues.includes(downlink.encoding)) {
          if (downlink.encoding !== 'base16') {
            return;
          }
          downlink.encoding = 'hex';
        }
        return downlink;
      }).filter(downlink => downlink);
      return downlinks;
    },
    operationalStatus() {
      return {
        0: this.$i18n_t('NiotaClassic.Devices.form.unset'),
        1: this.$i18n_t('NiotaClassic.Devices.form.inTransit'),
        2: this.$i18n_t('NiotaClassic.Devices.form.readyForOperation'),
        3: this.$i18n_t('NiotaClassic.Devices.form.inOperation'),
        4: this.$i18n_t('NiotaClassic.Devices.form.temporaryInactive'),
        5: this.$i18n_t('NiotaClassic.Devices.form.decommissioned')
      };
    },
    operationalStatusItems() {
      return Object.keys(this.operationalStatus).map(status => {
        return {
          text: this.operationalStatus[status],
          value: status
        };
      });
    },
    rawState() {
      return this.twinDataStates.find(state => state.key === '_raw') || {};
    },
    lastRawStateUpdate() {
      const isoDate = get(this.rawState, 'value.time');
      if (isoDate) {
        return new Date(isoDate);
      }
      return null;
    },
    getTypeNameOrDriverName() {
      return this.deviceDriverName || this.deviceTypeName;
    },
    deviceTypeId() {
      return get(this.dtwin, 'data.virtualDevice.deviceTypeId');
    },
    deviceDriverId() {
      return get(this.dtwin, 'data.virtualDevice.deviceDriverId', this.deviceTypeId);
    },
    deviceTypeName() {
      if (this.deviceTypeId) {
        return (this.deviceTypes.find(dt => dt.id === get(this.dtwin, 'data.virtualDevice.deviceTypeId')) || {}).title;
      }
      return null;
    },
    deviceDriverName() {
      return this.deviceDriver ? this.deviceDriver.title : null;
    },
    deviceTypeHasParser() {
      return get(this.selectedDeviceType, 'parser.config') !== undefined;
    },
    deviceDriverHasDefaultParser() {
      const versions = get(this.deviceDriver, 'versions') || [];
      return versions.some(v => v.version === 'default');
    },
    downlinkMethodParameters() {
      return get(this.bridgeManifest, 'commands.instance.sendPacket.parameters') || {};
    },
    downlinkMethodFields() {
      const keys = Object.keys(this.downlinkMethodParameters).filter(k => k !== 'deviceId');
      return keys.sort((a, b) => {
        const orderIdxA = get(this.downlinkMethodParameters, `[${a}].orderIdx`) || 0;
        const orderIdxB = get(this.downlinkMethodParameters, `[${b}].orderIdx`) || 0;
        if (orderIdxA < orderIdxB) {
          return -1;
        }
        if (orderIdxA > orderIdxB) {
          return 1;
        }
        return a.localeCompare(b);
      });
    },
    packetsMetricsDataComp() {
      return {
        common: {
          labels: this.packetMetricsData.common.labels
        },
        individual: [{
          color: this.customColor,
          label: this.$i18n_t('DigitalTwin.VirtualDevice.details.latestTimeRange'),
          data: this.packetMetricsData.individual[0].data,
          labels: this.packetMetricsData.individual[0].labels
        }, {
          color: '#aaa',
          label: this.$i18n_t('DigitalTwin.VirtualDevice.details.previousTimeRange'),
          data: this.packetMetricsData.individual[1].data,
          labels: this.packetMetricsData.individual[1].labels
        }]
      };
    },
    bridgeOutputFieldKeys() {
      if (!this.bridgeUpdateMethod && this.bridgeReadMethod) {
        return this.bridgeCreateFields.map(configKey => {
          if ((this.deviceDataFromBridge || {})[configKey]) {
            return configKey;
          }
          return null;
        }).filter(f => f);
      }
      return this.bridgeCreateFields;
    },
    useExternalData() {
      return get(this.bridgeManifest, 'functionScope.useExternalDataOnly', false);
    }
  },
  watch: {
    'dtwin.properties.groups': {
      handler(newVal, oldVal) {
        const removedGroup = oldVal.filter(g => !newVal.includes(g));
        const blockedRemovals = removedGroup.filter(g => this.templateGroups.includes(g));
        if (blockedRemovals.length) {
          this.dtwin.properties.groups = [...new Set([...newVal, ...blockedRemovals])];
        }
      }
    },
    deviceTypeName() {
      this.fetchDeviceDependentData();
    },
    deviceDriverName() {
      this.fetchDeviceDependentData();
    },
    twinDataStates: debounce(function () {
      // to enable a "live-packet-viewer", listen to updates of the twin-data-states
      // if there is an update, and the selected timerange is "up to now", forward an event to
      // the packet-history-viewer that will evaluate whether to perform a refresh
      if (this.timeRange.timeRangeToNow) {
        this.timeRange = {
          ...this.timeRange,
          isAutoRefresh: true,
          to: new Date() / 1000
        };
        // this.fetchTotalPacketCount();
      }
    }, 1000),
    currentPacket() {
      this.parsePacket();
    },
    dtwin: debounce(function () {
      this.setupInitialVDevice();
      const doNotParsePacket = false;
      this.fetchDeviceDependentData(doNotParsePacket);
    }, 1000),
    bridgeReadMethod() {
      this.fetchDeviceDependentData();
    },
    deviceDriversSearchInput: {
      handler: async function (searchValue) {
        try {
          this.isSearchingDeviceDrivers = true;
          const result = await ApiService.virtualDeviceDrivers.fetch({
            search: searchValue
          });
          this.deviceDriversSearchResult = result.deviceDrivers;
        } finally {
          this.isSearchingDeviceDrivers = false;
        }
      },
      immediate: true
    },
    deviceDriverId: {
      handler: async function () {
        this.fetchDeviceDriver();
        this.parsePacket();
      },
      immediate: true
    },
    deviceDriverHasDefaultParser() {
      this.parsePacket();
    },
    deviceTypeHasParser() {
      this.parsePacket();
    },
    bridgeManifest() {
      this.parsePacket();
    }
  },
  created() {
    this.setupInitialVDevice();
  },
  mounted() {
    this.fetchDeviceTypes();
    this.fetchDeviceGroups();
    // this.fetchTotalPacketCount(); // Get Number of all packets.
    this.fetchPacketCountHistory(); // Get Packet History Chart Data
    this.fetchDeviceDependentData();
  },
  methods: {
    get,
    clickRowHandler(item) {
      this.currentPacket = item;
    },
    selectPredefinedDownlink(item) {
      if (item === undefined) {
        // was cleared
        this.downlinkParameters['payload'] = null;
        this.downlinkParameters['data'] = null;
        this.downlinkParameters['encoding'] = 'utf8';
        this.downlinkParameters['port'] = 0;
        this.downlinkParameters['eui'] = null;
        return;
      }
      const download = item || {};
      if (this.bridgeManifest.displayName === 'Firefly') {
        this.downlinkParameters['payload'] = download.payload;
      } else if (this.bridgeManifest.displayName === 'Loriot') {
        this.downlinkParameters['data'] = download.payload;
      }
      this.downlinkParameters['encoding'] = download.encoding || 'hex';
      this.downlinkParameters['port'] = parseInt(download.port, 10) || 1;
      this.downlinkParameters['eui'] = get(this.dtwin, 'properties.bridge.deviceData.device_id') || '';
      this.downlinksRenderCacheBuster += 1;
    },
    setupInitialVDevice() {
      if (!get(this.dtwin, 'properties.bridge')) {
        this.dtwin.properties = {
          ...(get(this.dtwin, 'properties') || {}),
          bridge: {
            deviceData: {}
          }
        };
      }
      if (!get(this.dtwin, 'data.virtualDevice')) {
        this.dtwin.data = {
          ...(get(this.dtwin, 'data') || {}),
          virtualDevice: {}
        };
      }
    },
    deviceConfigChanged() {
      // force a UI-refresh of the fields (dependent config fields)
      this.dtwin = {
        ...this.dtwin,
        properties: cloneDeep(this.dtwin.properties)
      };
      this.$emit('change-bridge-params', this.dtwin.properties.bridge.deviceData);
    },
    noop() {
      // eslint-disable-next-line no-console
      console.debug('no op');
    },
    setTimeRange(data) {
      const timeRangeToNow = new Date() - new Date(data.to * 1000) < 60 * 1000;
      this.currentPacket = null;
      this.timeRange = {
        from: data.from,
        to: data.to,
        timeRangeToNow,
        isAutoRefresh: timeRangeToNow
      };
      this.fetchPacketCountHistory();
    },
    fetchPacketCountHistory() {
      if (!this.dtwin.id || !this.rawState.id || this.isLoadingPacketMetrics) {
        return;
      }
      this.isLoadingPacketMetrics = true;
      let lclTimeFrom;
      if (!this.timeRange.from) {
        lclTimeFrom = new Date(new Date() - 86400000 * 62) / 1000;
      } else {
        lclTimeFrom = this.timeRange.from;
      }
      const rangeStart = new Date(lclTimeFrom * 1000);
      const interval = new Date(this.timeRange.to * 1000) - rangeStart;
      const priorRangeStart = new Date(rangeStart - interval);

      // note: the default options of time-range-picker may be 1min short of the full interval,
      // i.e. "yesterday" picks 12:00 AM ... to ... 11:59 PM
      // so subtract a minute if interval is greater than a day, to be sure
      const oneDay = (86400 - 60) * 1000;
      const oneHour = 3600 * 1000;
      const fifteenMinutes = 900 * 1000;
      const duration = interval >= oneDay * 2 ? '1d' : interval >= oneHour * 2 ? '1h' : interval >= fifteenMinutes * 2 ? '15m' : '5m';

      // fetch the current interval, as well as the "parallel" history exactly 1 interval prior
      Promise.all([ApiService.virtualDevicePacketStatistics(this, this.dtwin.id, rangeStart, new Date(this.timeRange.to * 1000), duration), ApiService.virtualDevicePacketStatistics(this, this.dtwin.id, priorRangeStart, rangeStart, duration)]).then(data => {
        this.preparePacketMetricsData(data[0].total, data[1].total, 'packetMetricsOptions', 'packetMetricsData', true);
        this.isLoadingPacketMetrics = false;
      }).catch(error => {
        console.error(error);
      });
    },
    relativeTime(date) {
      if (date) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        return moment(date).fromNow();
      }
      return '-';
    },
    fetchDeviceTypes() {
      if (!this.deviceTypeId || this.isDeviceDriverActive) {
        return;
      }
      NiotaClassicApiService.deviceTypesGetPlain().then(res => {
        this.deviceTypes = res.data.devicetypes;
      }).catch(error => {
        this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.errorFindingAccounts'), 'error');
        console.error(error);
      });
    },
    async fetchDeviceDriver() {
      if (!this.deviceDriverId || !this.isDeviceDriverActive) {
        return;
      }
      this.deviceDriver = await ApiService.virtualDeviceDrivers.fetchOneById(this.deviceDriverId);
    },
    fetchDeviceDependentData() {
      if (this.deviceTypeName) {
        NiotaClassicApiService.deviceTypeGetDetails(this.dtwin.data.virtualDevice.deviceTypeId).then(res => {
          this.selectedDeviceType = res.data.devicetype;
        }).catch(error => {
          this.$snack(this.$i18n_t('NiotaClassic.DeviceTypes.snacks.fetchDetailsFailed'), 'error');
          console.error(error);
        });
      }
      // in case the bridge has no update, check if we shall show readonly data here by fetching the
      // current config straight from the bridge
      if (!this.deviceDataFromBridge && get(this.dtwin, 'properties.bridge.deviceData.device_id') && (this.useExternalData || this.bridgeReadMethod && !this.bridgeUpdateMethod)) {
        if (!this.bridgeReadMethod) {
          return;
        }
        const parameterNames = Object.keys(get(this.bridgeManifest, `commands.instance.${this.bridgeReadMethod}.parameters`, {}));
        let parameters = {};
        if (parameterNames.length >= 1) {
          parameters[parameterNames[0]] = get(this.dtwin, 'properties.bridge.deviceData.device_id');
        }
        ApiService.bridgeCommand(this.connectedBridge.id, this.bridgeReadMethod, parameters).then(result => {
          if (!result.error && result.data) {
            this.deviceDataFromBridge = result.data;
            if (this.useExternalData) {
              if (this.connectedBridge.type === 'bridge-wireless-mbus-decoder' && this.dtwin.properties.bridge.deviceData.key) {
                // copy key from deviceData as it cannot be provided from remote system
                this.deviceDataFromBridge.key = this.dtwin.properties.bridge.deviceData.key;
              }
              if (this.connectedBridge.type === 'bridge-lwm2m-server' && this.dtwin.properties.bridge.deviceData.key) {
                // copy key from deviceData as it cannot be provided from remote system
                this.deviceDataFromBridge.key = this.dtwin.properties.bridge.deviceData.key;
              }
              if (!isEqual(this.dtwin.properties.bridge.deviceData, result.data)) {
                if (this.dtwin.properties.bridge.deviceData.connection_server_type === 'bootstrap') {
                  // extract device_id from data without using it, as it comes from external system with suffix '-bs'
                  // eslint-disable-next-line no-unused-vars
                  const {
                    device_id,
                    ...data
                  } = result.data;
                  Object.assign(this.dtwin.properties.bridge.deviceData, data);
                } else {
                  this.dtwin.properties.bridge.deviceData = result.data;
                }
                this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceDataHasBeenChanged', {
                  connectorName: this.connectedBridge.name
                }), 'info');
              }
            }
          } else {
            this.$snack(get(result, 'data.error') || result.errorMessage, 'error');
          }
        }).catch(error => {
          if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
            this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
          } else {
            this.$snack(this.$i18n_t('Common.digitalTwins.twinCreatedFail'), 'error');
          }
        });
      }
    },
    parsePacket() {
      if (!(this.deviceDriverHasDefaultParser || this.deviceTypeHasParser || this.deviceDriverId)) {
        return;
      }
      const payload = get(this.currentPacket, `value.${get(this.bridgeManifest, 'virtualDevice.deviceTypes.packetParser.valueMapping.payload')}`);
      if (!payload) {
        return;
      }

      // For legacy LoRaWAN parser in N1 we need a port
      let port = null;
      if (get(this.bridgeManifest, 'virtualDevice.deviceTypes.packetParser.valueMapping.port')) {
        port = get(this.currentPacket, `value.${this.bridgeManifest.virtualDevice.deviceTypes.packetParser.valueMapping.port}`);
      }
      // If the port is unset, we give it a default: 1
      if (!port) {
        port = 1;
      }
      let twinId = -1;
      if (this.dtwin.id) {
        twinId = this.dtwin.id;
      }
      let dataTimestamp;
      if (this.currentPacket.value.timestamp) {
        dataTimestamp = this.currentPacket.value.timestamp;
      }
      const parserPayload = {
        payload,
        meta: {
          device_id: twinId,
          device_driver_id: this.deviceDriverId,
          device_type_id: this.deviceTypeId,
          source_time: dataTimestamp,
          lora: {
            fport: port
          }
        }
      };
      const parserMethod = this.isDeviceDriverActive ? ApiService.deviceDriverParsePayload(this.deviceDriverId, parserPayload) : ApiService.virtualDeviceDeviceTypesParsePayloadN1(this.deviceDriverId, parserPayload);
      parserMethod.then(res => {
        const error = get(res, 'error');
        const parsedPacket = get(res, 'parsed', get(res, 'result', error));
        if (parsedPacket.__niotix_internal_logs) {
          delete parsedPacket.__niotix_internal_logs;
        }
        this.currentParsedPacket = parsedPacket;
      }).catch(() => {
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.parser.internalServerError'), 'error');
      });
    },
    fetchTotalPacketCount() {
      return;
      // Taken out by MWa because VDs with very high amount of packets (like half a million)
      // kills the influx, it causes OOM Outages on cubbage.aws
      // if (!this.dtwin.id || !this.rawState.id || this.isLoadingTotalCount) {
      //   return;
      // }
      // this.isLoadingTotalCount = true;
      // const timeRangeQueryItems = {
      //   and: [
      //     { path: '_time', operator: 'gte', value: this.dtwin.createdAt },
      //     { path: '_time', operator: 'lte', value: new Date().toISOString() },
      //   ],
      // };
      // ApiService.influxStatesInfluxSearch(this, [this.dtwin.id], [this.rawState.id], timeRangeQueryItems, 0, 1, [
      //   { path: '_time', direction: 'DESC' },
      // ])
      //   .then((data) => {
      //     this.totalUplinkPacketCount = data.totalCount;
      //   })
      //   .catch((error) => { console.error(error); })
      //   .finally(() => {
      //     this.isLoadingTotalCount = false;
      //   });
    },
    fetchDeviceGroups() {
      ApiService.virtualDeviceFetchGroups().then(res => {
        this.deviceGroups = res.groupList || [];
        if (this.isCreatedByTemplate) {
          this.deviceGroups = this.deviceGroups.map(group => {
            return {
              text: group,
              value: group,
              disabled: this.templateGroups.includes(group)
            };
          });
        }
      }).catch(error => {
        this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.errorFindingAccounts'), 'error');
        console.error(error);
      });
    },
    addNewDeviceGroup() {
      let matchingGroup = this.deviceGroups.find(g => {
        const currentValue = this.isCreatedByTemplate ? g.value : g;
        return currentValue.toLowerCase().trim() === this.newDeviceGroup.toLowerCase().trim();
      });
      if (!matchingGroup) {
        matchingGroup = this.newDeviceGroup.trim();
        this.deviceGroups = [...this.deviceGroups, this.isCreatedByTemplate ? {
          text: matchingGroup,
          value: matchingGroup
        } : matchingGroup];
      }
      this.dtwin.properties.groups = [...this.dtwin.properties.groups, matchingGroup];
      this.newDeviceGroup = '';
    },
    sendDownlinkPacket() {
      ApiService.bridgeCommand(this.dtwin.properties.bridge.configId, 'sendPacket', {
        ...this.downlinkParameters,
        deviceId: this.dtwin.properties.bridge.deviceData.device_id
      }).then(res => {
        if (res.error === false) {
          this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.sentDownlink'), 'success');
        } else {
          throw {
            response: {
              data: {
                err: get(res, 'data[0].message') || res.data || res.errorMessage
              }
            }
          };
        }
      }).catch(error => {
        const errorMsg = get(error, 'response.errorMessage') || error;
        if (typeof errorMsg === 'string') {
          this.$snack(errorMsg, 'error');
        } else {
          this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.failedDownlink'), 'error');
        }
      });
    },
    getDependsOnFieldValue(createFields, currentField, deviceData) {
      const dependsOnFieldName = get(createFields[currentField], 'dependsOnField');
      if (!dependsOnFieldName) {
        return null;
      }
      return deviceData[dependsOnFieldName];
    },
    getRequiredPermission(createFields, currentField) {
      const fieldOptions = createFields[currentField];
      const requiredPermissionFromManifest = get(fieldOptions.stringOptions, 'requiredPermission') || '';
      return requiredPermissionFromManifest;
    },
    hasRequiredPermission(permissionFromManifest, requiredPermission) {
      if (permissionFromManifest === 'VirtualDeviceKey.read' && requiredPermission) {
        return true;
      }
      return false;
    },
    isPermissionBasedField(createFields, currentField) {
      const fieldOptions = createFields[currentField];
      const requiresPermission = get(fieldOptions.stringOptions, 'requiresPermission');
      if (requiresPermission) {
        return true;
      }
      return false;
    },
    restrictCharacters(event) {
      if (event.key === ',' || event.key === ';') {
        event.preventDefault();
      }
    },
    async changeActiveDeviceDriverId(selected) {
      if (this.isDeviceDriverActive) {
        this.$emit('change-device-driver', selected);
      } else {
        this.$emit('change-device-type', selected);
      }
    },
    unlinkDeviceTemplate() {
      ApiService.virtualDeviceUnlinkFromTemplate(this, this.dtwin.id).then(result => {
        this.$snack(result.message, 'success');
        this.$emit('unlink-device-template');
      }).catch(error => {
        this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.errorFindingAccounts'), 'error');
        console.error(error);
      });
    }
  }
};